import { Module, VuexModule, Action, Mutation } from 'vuex-module-decorators';
import TableOfContents from '@/types/TableOfContents';
import { Id } from '@/types/Slide';
import store from '@/store';
import { LOCK_CONST, STORE_CONST } from '@/constants';
import { getFromLocalStorage, saveToLocalStorage } from '@/utils/storage';
import BTB_CONF from '@/btb-conf.json';
import { BtbConf } from '@/types/BtbConf';
import Timer from 'easytimer.js';

import toc from '@/views/toc.json';
import Bookmark from '@/models/Bookmark';

export interface IAppState {
  tableOfContents: TableOfContents;
  currentIndex: number;
  biggestIndex: number;
  currentSlide: Id;
  alreadyVisitedPagesHash: { [id: string]: boolean };
  sidebarOpen: boolean;
  isFullscreen: boolean;
  savedData: any;
  lockMode: number;
  totalScore: number;
  score: number;
  version: string;
  started: boolean;
  hasNeverChangedCaptionsSettings: boolean;
}

@Module({ namespaced: true, name: STORE_CONST.APP_MODULE_NAME, store })
export class AppModule extends VuexModule implements IAppState {
  tableOfContents = toc;
  currentIndex = 0;
  biggestIndex = 0;
  currentSlide = this.tableOfContents.slides[0].id;
  alreadyVisitedPagesHash: { [id: string]: boolean } = {
    [this.currentSlide]: true
  };
  sidebarOpen = false;
  isFullscreen = false;

  savedData: any = undefined;

  lockMode = LOCK_CONST.UNLOCKED;

  BTB_CONF: BtbConf = BTB_CONF;

  totalScore: number = 0.0;
  score: number = 0.0;
  completionStatus: string = ''
  successStatus: string = ''

  windowWidth: number = window.innerWidth;

  version: string = `B${this.BTB_CONF.builderInfo.version}-P${this.BTB_CONF.playerInfo.version}-C${this.BTB_CONF.courseInfo.version}`;

  started: boolean = false;

  hasNeverChangedCaptionsSettings = true;

  // Analytics
  trainingTimer : Timer = new Timer()
  userId !: string;

  get getLockMode() {
    return this.lockMode;
  }

  get getSideBarOpen() {
    return this.sidebarOpen;
  }

  get getAlreadyVisitedPages() {
    return this.alreadyVisitedPagesHash;
  }

  get getCurrentSlide() {
    return this.currentSlide;
  }

  get getCurrentSlideTitle() {
    return this.tableOfContents.slides[this.currentIndex].title;
  }

  get getCurrentIndex() {
    return this.currentIndex;
  }

  get getBiggestIndex() {
    return this.biggestIndex;
  }

  get getTableOfContents() {
    return this.tableOfContents;
  }

  get hasStarted() {
    return this.started;
  }

  get isLockedForward(): boolean {
    return this.getLockMode !== LOCK_CONST.UNLOCKED;
  }

  get isTotallyLocked(): boolean {
    return this.getLockMode === LOCK_CONST.TOTALLY_LOCKED;
  }

  get getScore(): number {
    return this.score;
  }

  get getTotalScore(): number {
    return this.totalScore;
  }

  get getScaledScore(): number {
    return this.getScore && this.getTotalScore ? (this.getScore / this.getTotalScore) : 0;
  }

  get getIsMobile(): boolean {
    return this.windowWidth < 1024;
  }

  get getVersion(): string {
    return this.version;
  }

  get getIsFullscreen(): boolean {
    return this.isFullscreen;
  }
  get getTrainingTimer(): Timer {
    return this.trainingTimer;
  }
  get getUserId(): string {
    return this.userId;
  }
  get getLastIndex(): number {
    return (this.getTableOfContents.slides.length - 1);
  }

  @Mutation
  SET_SLIDE(id: Id) {
    this.currentSlide = id;
  }

  @Mutation
  SET_LOCK(lockMode: number) {
    if (this.BTB_CONF.debug) this.lockMode = LOCK_CONST.UNLOCKED;
    else this.lockMode = lockMode;
  }

  @Mutation
  SET_INDEX(index: number) {
    if (index < 0) index = 0;
    this.currentIndex = index;
  }

  @Mutation
  SET_BIGGEST_INDEX(index: number) {
    this.biggestIndex = index;
  }

  @Mutation
  FREE_SLIDE(id: Id) {
    this.alreadyVisitedPagesHash = {
      ...this.alreadyVisitedPagesHash,
      [id]: true
    };
  }

  @Mutation
  OPEN_SIDEBAR() {
    this.sidebarOpen = true;
  }

  @Mutation
  CLOSE_SIDEBAR() {
    this.sidebarOpen = false;
  }

  @Mutation
  UPDATE_DATA() {
    const trainingData = getFromLocalStorage(store.getters.storagePrefixCourse);
    if (!trainingData) this.savedData = undefined;
    else
      this.savedData =
        this.currentSlide in trainingData
          ? trainingData[this.currentSlide]
          : undefined;
  }

  @Mutation
  SAVE_DATA(data: any) {
    this.savedData = data;
  }

  @Mutation
  ADD_RIGHT() {
    this.score = this.score + 1;
    this.totalScore = this.totalScore + 1;
  }

  @Mutation
  ADD_WRONG() {
    this.totalScore = this.totalScore + 1;
  }

  @Mutation
  FIX_SCORE(score: number) {
    this.score = score;
  }

  @Mutation
  FIX_TOTAL_SCORE(totalScore: number) {
    this.totalScore = totalScore;
  }

  @Mutation
  FIX_SCORM_STATUS(scormStatus: any) {
    const {completionStatus, successStatus} = scormStatus;
    this.completionStatus = completionStatus;
    this.successStatus = successStatus;
  }

  @Mutation
  UPDATE_WINDOW_WIDTH(width: number) {
    this.windowWidth = width;
  }

  @Mutation
  SET_IS_FULLSCREEN(isFullscreen: boolean) {
    this.isFullscreen = isFullscreen;
  }

  @Mutation
  SET_HAS_STARTED(started: boolean) {
    this.started = started;
  }

  @Mutation
  SET_INTRO(intro: any) {
    this.tableOfContents.slides.unshift(intro);
  }

  @Mutation
  SET_OUTRO(outro: any) {
    this.tableOfContents.slides.pop();
    this.tableOfContents.slides.push(outro);
  }



  @Mutation
  SET_TRAINING_TIMER(timer:Timer){
    this.trainingTimer = timer;
  }

  @Mutation
  SET_USER_ID(user:string){
    this.userId = user;
  }

  @Mutation
  SET_HAS_NEVER_CHANGED_CAPTIONS_SETTINGS(hasNeverChangedCaptions:boolean){
    this.hasNeverChangedCaptionsSettings = hasNeverChangedCaptions;
  }

  @Action
  addIntro(title: string) {
    this.context.commit('SET_INTRO', { id: 'BTB_INTRO', title: title });
  }

  @Action
  replaceOutro(title: string) {
    this.context.commit('SET_OUTRO', { id: 'BTB_OUTRO', title: title });
  }

  @Action({ rawError: true })
  setCurrentSlide(id: Id) {
    if (id === this.getCurrentSlide) return;
    this.context.commit('SET_SLIDE', id);
    this.context.commit('SET_LOCK', LOCK_CONST.UNLOCKED);
    this.context.commit('UPDATE_DATA');
  }

  @Action({ rawError: true })
  setCurrentIndex(index: number) {
    if (index === this.getCurrentIndex) return;
    this.context.commit('SET_INDEX', index);
  }

  @Action({ rawError: true })
  setBiggestIndex(index: number) {
    if (index > this.getBiggestIndex)
      this.context.commit('SET_BIGGEST_INDEX', index);
  }

  @Action({ rawError: true })
  setUserId(userId: string) {
    this.context.commit('SET_USER_ID', userId);
  }

  @Action({ rawError: true })
  freeSlide(id: Id) {
    this.context.commit('FREE_SLIDE', id);
  }

  @Action({ rawError: true })
  openSideBar() {
    this.context.commit('OPEN_SIDEBAR');
  }

  @Action({ rawError: true })
  closeSideBar() {
    this.context.commit('CLOSE_SIDEBAR');
  }

  @Action({ rawError: true })
  setLock(lockMode: number) {
    if (this.getCurrentIndex < this.getBiggestIndex) return;
    if (lockMode === LOCK_CONST.UNLOCKED && this.getCurrentIndex < this.getLastIndex)
      this.setBiggestIndex(this.getCurrentIndex + 1);

    this.context.commit('SET_LOCK', lockMode);
  }

  @Action({ rawError: true })
  increment(delta: number) {
    if (this.isTotallyLocked) return;
    if (this.isLockedForward && delta > 0) return;
    this.setCurrentIndex(this.currentIndex + delta);
    this.setBiggestIndex(this.currentIndex);
    this.setCurrentSlide(this.tableOfContents.slides[this.currentIndex].id);
    this.freeSlide(this.tableOfContents.slides[this.currentIndex].id);
  }

  @Action({ rawError: true })
  goTo(args: [Id, number]) {
    const id = args[0];
    const index = args[1];
    if (this.isTotallyLocked) return;
    if (!this.getAlreadyVisitedPages[id]) return;
    if (this.isLockedForward && index > this.getBiggestIndex) return;
    this.setCurrentSlide(id);
    this.setCurrentIndex(index);
    if (this.getIsMobile) this.closeSideBar();
  }

  @Action({ rawError: true })
  goToBookmark(bookmark: Bookmark) {
    if (this.BTB_CONF.debug)
      bookmark.biggestIndex = this.getTableOfContents.slides.length - 1;

    this.setCurrentSlide(
      this.getTableOfContents.slides[bookmark.currentIndex] ? 
        this.getTableOfContents.slides[bookmark.currentIndex].id : 
        this.getTableOfContents.slides[0].id
    );
    if (bookmark.lastSlideState) 
      this.saveData(bookmark.lastSlideState);

    this.setCurrentIndex(bookmark.currentIndex);
    this.context.commit('SET_BIGGEST_INDEX', bookmark.biggestIndex);
    for (let index = 0; index <= bookmark.biggestIndex; index++) 
      if (index < this.getTableOfContents.slides.length)
        this.freeSlide(this.getTableOfContents.slides[index].id);
    
    this.fixScores(bookmark);
  }

  @Action({ rawError: true })
  fixScores(bookmark: Bookmark) {
    this.context.commit('FIX_SCORE', bookmark.score);
    this.context.commit('FIX_TOTAL_SCORE', bookmark.total);
  }

  @Action({ rawError: true })
  fixScormStatus(statuses: {completionStatus: string, successStatus: string}) {
    const {completionStatus, successStatus} = statuses;
    this.context.commit('FIX_SCORM_STATUS', {completionStatus,successStatus} );
  }

  @Action({ rawError: true })
  saveData(data: any) {
    let trainingData = getFromLocalStorage(store.getters.storagePrefixCourse);
    if (!trainingData) trainingData = {};
    trainingData[this.getCurrentSlide] = data;
    saveToLocalStorage(store.getters.storagePrefixCourse, trainingData);
    this.context.commit('SAVE_DATA', data);
  }

  get getSavedData(): any {
    return this.savedData;
  }

  @Action({ rawError: true })
  chooseAnswer(isRight: boolean) {
    isRight
      ? this.context.commit('ADD_RIGHT')
      : this.context.commit('ADD_WRONG');
  }

  @Action({ rawError: true })
  resetScore() {
    this.context.commit('FIX_SCORE', 0);
    this.context.commit('FIX_TOTAL_SCORE', 0);
  }

  @Action({ rawError: true })
  updateData() {
    this.context.commit('UPDATE_DATA');
  }

  @Action({ rawError: true })
  cleanData() {
    saveToLocalStorage(store.getters.storagePrefixCourse, {});
    this.context.commit('UPDATE_DATA');
  }

  @Action({ rawError: true })
  updateWindowWidth(width: number) {
    this.context.commit('UPDATE_WINDOW_WIDTH', width);
  }

  @Action({ rawError: true })
  toggleFullscreen() {
    this.context.commit('SET_IS_FULLSCREEN', !this.isFullscreen);
  }

  @Action({ rawError: true })
  setHasStarted(started: boolean) {
    this.context.commit('SET_HAS_STARTED', started);
  }

  @Action({ rawError: true })
  setTrainingTimer(timer:Timer){
    this.context.commit('SET_TRAINING_TIMER',timer);
  }

  @Action({ rawError: true })
  setHasNeverChangedCaptionsSettings(hasNeverChangedCaptions:boolean){
    this.context.commit('SET_HAS_NEVER_CHANGED_CAPTIONS_SETTINGS',hasNeverChangedCaptions);
  }
}